
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Input, Dropdown, List, Spin, Tooltip, Divider } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import debounce from 'lodash.debounce';
import styled, { createGlobalStyle } from 'styled-components';
import Iconmoon from "../../Components/IconsFiles/icon";

// ✅ Global overrides for Ant Design styles
const GlobalStyle = createGlobalStyle`
  .ant-dropdown,
  .ant-dropdown-menu {
    text-align: left !important;
  }

  .ant-list-item {
    align-items: flex-start !important;
  }
`;

// ✅ Styled Input box
const StyledInput = styled(Input)`
  &.ant-input-affix-wrapper {
    height: 44px;
    font-size: 16px;
    border-radius: 6px;
    padding: 4px 11px;
    border: 1px solid #d9d9d9;
    transition: border-color 0.3s, box-shadow 0.3s;

    &:hover {
      border-color: #ea655b !important;
      box-shadow: none !important;
    }

    &.ant-input-affix-wrapper-focused {
      border-color: #ea655b !important;
      box-shadow: 0 0 0 2px rgba(255, 0, 0, 0.3) !important;
    }

    input.ant-input {
      font-size: 16px;
      line-height: 1;
      height: 100%;
      padding: 0;
    }

    .ant-input-suffix {
      color: #999;
    }
  }
`;

// ✅ Custom red-colored Spin loader
const RedSpin = styled(Spin)`
  .ant-spin-dot-item {
    background-color: #ea655b !important;
  }
`;

// ✅ Highlight matched text
const highlightMatch = (text, keyword) => {
  if (!text || !keyword) return text;
  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedKeyword})`, 'gi');
  return text.replace(
    regex,
    '<span style="color: #ea655b; font-weight: 600;">$1</span>'
  );
};

const SearchDropdown = ({ onSearch, onSelect, placeholder = 'Search...' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState(0);

  const wrapperRef = useRef(null);

  // ✅ Adjust dropdown width dynamically
  useEffect(() => {
    const updateWidth = () => {
      if (wrapperRef.current) {
        setDropdownWidth(wrapperRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // ✅ Fetch results with debounce
  const fetchResults = async (value) => {
    if (!value) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const data = await onSearch(value);
      setResults(data);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(debounce(fetchResults, 400), []);

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    debouncedSearch(value);
  };

  // ✅ Dropdown content
  const dropdownContent = (
    <div
      style={{
        maxHeight: 300,
        overflowY: 'auto',
        overflowX: 'hidden', // ✅ Fix horizontal overflow
        background: '#fff',
        border: '1px solid #f0f0f0',
        borderRadius: 4,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        width: dropdownWidth,
        padding: '8px 16px',
        textAlign: 'left',
        boxSizing: 'border-box',
      }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <RedSpin size="small" />
        </div>
      ) : results.length ? (
        Object.entries(
          results.reduce((groups, item) => {
            const key = item.module || 'Other';
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
            return groups;
          }, {})
        ).map(([moduleName, items], index) => (
          <div key={moduleName} style={{ marginBottom: 12 }}>
            {index > 0 && <Divider style={{ margin: '12px 0' }} />}

            {/* ✅ Module Header */}
            <div
              style={{
                fontSize: 14,
                marginBottom: 6,
                color: '#555',
                textAlign: 'left',
                // fontWeight: 600,
              }}
            >
              {moduleName?.toUpperCase()}
            </div>

            <List
              size="small"
              split={false}
              dataSource={items}
              renderItem={(item) => (
                <List.Item
                  key={item.id}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '8px 0',
                  }}
                  onClick={() => {
                    onSelect?.(item);
                    setQuery('');
                    setResults([]);
                    if (item.url) {
                      window.open(item.url, '_blank');
                    }
                  }}
                >
                  {/* ✅ Left Icon */}
                  <div style={{ flexShrink: 0, paddingTop: 12, paddingRight: 5 }}>
                    <Iconmoon icon="box-pack" size={14} color="gray" />
                  </div>

                  {/* ✅ Right Text Section */}
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                    }}
                  >
                    {/* Label */}
                    <Tooltip title={item.label}>
                      <div
                        style={{
                          fontSize: 14,
                          color: '#333',
                          lineHeight: 1.4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%', // ✅ Prevents overflow
                        }}
                        dangerouslySetInnerHTML={{
                          __html: highlightMatch(item.label, query),
                        }}
                      />
                    </Tooltip>

                    

                    {/* Section Path */}
                    {item.section && (
                      <div
                        style={{
                          fontSize: 12,
                          color: '#888',
                          marginTop: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%', // ✅ Prevents overflow
                        }}
                      >
                        {item.section}
                      </div>
                    )}

                    {/* Headline */}
                    {item.headline && (
                      <div
                        style={{
                          fontSize: 12,
                          color: '#666',
                          marginTop: 4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.4,
                          maxWidth: '100%',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: highlightMatch(item.headline, query),
                        }}
                      />
                    )}
                  </div>
                </List.Item>
              )}
            />
          </div>
        ))
      ) : (
        <div style={{ padding: 10, color: '#999' }}>No results found</div>
      )}
    </div>
  );

  return (
    <div style={{ width: '100%', maxWidth: 500 }} ref={wrapperRef}>
      <GlobalStyle />
      <Dropdown
        overlay={dropdownContent}
        open={query.length > 0 && (results.length > 0 || loading)}
        trigger={['click']}
        placement="bottomLeft"
      >
        <StyledInput
          placeholder={placeholder}
          style={{ height: 40 }}
          suffix={<SearchOutlined />}
          value={query}
          onChange={handleChange}
        />
      </Dropdown>
    </div>
  );
};

export default SearchDropdown;